if (!customElements.get("trustpilot-rating")) {
  customElements.define(
    "trustpilot-rating",
    class TrustpilotRating extends HTMLElement {
      constructor() {
        super();

        this.id = this.getAttribute("data-tp-id");
        this.buid = this.getAttribute("data-buid");
        this.queryId = this.getAttribute("data-query-id");
        this.avgRating = this.querySelector(".tp-avg-rating");
        this.content = this.querySelector(".trustpilot-rating__content");

        this.scrollToReviews =
          this.getAttribute("data-scroll-to-reviews") === "true";
        this.scrollToReviewsSelector = ".section-trustpilot-product-reviews";
      }

      connectedCallback() {
        // only fetch if element comes into view
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // fetch rating
              this.getRating();
              observer.disconnect();
            }
          });
        });

        observer.observe(this);

        // scroll to reviews
        if (this.scrollToReviews) {
          this.addEventListener("click", this.scrollToReviewsElement);
        }
      }

      calculateRoundedRating(reviews) {
        if (!reviews || reviews.total === 0) return 0;

        const totalScore =
          (reviews.oneStar || 0) * 1 +
          (reviews.twoStars || 0) * 2 +
          (reviews.threeStars || 0) * 3 +
          (reviews.fourStars || 0) * 4 +
          (reviews.fiveStars || 0) * 5;

        const avg = totalScore / reviews.total;

        return Math.round(avg * 10) / 10;
      }

      async getRating() {
        if (!this.queryId) {
          this.classList.add("hidden");
          return;
        }
        const base = `https://api.trustpilot.com/v1/product-reviews/business-units/${this.buid}`;
        const normalUrl = `${base}?sku=${this.queryId}`;
        const importedUrl = `${base}/imported-reviews-summaries?sku=${this.queryId}`;

        try {
          const headers = {
            apikey: window.tpk,
            "Content-Type": "application/json",
          };

          const [normalRes, importedRes] = await Promise.all([
            fetch(normalUrl, { headers }),
            fetch(importedUrl, { headers }),
          ]);

          if (!normalRes.ok || !importedRes.ok) {
            this.classList.add("hidden");
            throw new Error("Trustpilot rating fetch failed");
          }

          const normalData = await normalRes.json();
          const importedData = await importedRes.json();

          const finalRating = this.mergeAverages(normalData, importedData);
          this.renderRating(finalRating);
        } catch (error) {
          console.error("Trustpilot rating fetch error:", error);
          this.classList.add("hidden");
        }
      }

      mergeAverages(normal, imported) {
        const n = normal.numberOfReviews || {};
        const i = imported.numberOfReviews || {};

        const totalNormal = n.total || 0;
        const totalImported = i.total || 0;
        const finalTotal = totalNormal + totalImported;

        if (finalTotal === 0) {
          return { average: 0, count: 0 };
        }

        const score = (obj) =>
          (obj.oneStar || 0) * 1 +
          (obj.twoStars || 0) * 2 +
          (obj.threeStars || 0) * 3 +
          (obj.fourStars || 0) * 4 +
          (obj.fiveStars || 0) * 5;

        const finalScore = score(n) + score(i);

        return {
          average: Math.round((finalScore / finalTotal) * 10) / 10,
          count: finalTotal,
        };
      }

      renderRating({ average, count }) {
        if (!this.content || !this.avgRating || average <= 0) {
          this.classList.add("hidden");
          return;
        }

        this.avgRating.textContent = average;

        const countEl = this.querySelector(".tp-count");
        if (countEl) {
          const template = countEl.dataset.tpText;
          const plural = count === 1 ? "" : "s";

          const text = template
            .replace("{{ count }}", count)
            .replace("{{ plural }}", plural);

          countEl.innerHTML = text.replace(
            /(\d+\s+review[s]?)/,
            "<span>$1</span>",
          );
        }

        this.content.classList.remove("hidden");
      }

      scrollToReviewsElement() {
        const reviewsElement = document.querySelector(
          this.scrollToReviewsSelector,
        );
        if (!reviewsElement) return;

        let offset = 0;

        const stickyHeader = document.querySelector("header");
        if (stickyHeader) {
          offset += stickyHeader.offsetHeight;
        }

        const pdpStickyHeader = document.querySelector(".pdp-sticky-header");
        if (pdpStickyHeader) {
          offset += pdpStickyHeader.offsetHeight;
        }

        const elementTop =
          reviewsElement.getBoundingClientRect().top + window.pageYOffset;
        const scrollTo = elementTop - offset;

        window.scrollTo({
          top: scrollTo,
          behavior: "smooth",
        });
      }
    },
  );
}
